import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Plus, Trash2, GripVertical, Sparkles, BookOpen, Loader2, Wand2, PenLine } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default skeletons by genre (fallback)
const DEFAULT_SKELETONS = {
  // SHORT STORY - Compact, powerful (3-5 chapters, ~1,500-3,000 words each)
  shortstory: [
    { title: "The Hook", tips: "Start in medias res. Drop readers into action. Hemingway's iceberg theory: show 10%, suggest 90%" },
    { title: "Rising Tension", tips: "Escalate conflict quickly. Every sentence must earn its place. Use Chekhov's gun principle" },
    { title: "The Climax", tips: "Deliver your emotional punch. This is what readers will remember. Make it visceral" },
    { title: "Resolution", tips: "End with resonance, not explanation. Leave readers thinking. O. Henry twist or quiet revelation" },
  ],
  
  // MY LIFE STORY - Autobiography from birth to present (10-15 chapters)
  lifestory: [
    { title: "Where It All Began", tips: "Your birth story, family origins, earliest memories. Set the stage for who you'd become" },
    { title: "Childhood Dreams", tips: "Ages 5-10. First friends, school, family dynamics. What shaped your worldview?" },
    { title: "Coming of Age", tips: "Ages 11-18. Adolescence, identity formation, pivotal moments that defined you" },
    { title: "First Steps into the World", tips: "Ages 18-25. Leaving home, early career/education, first love, finding yourself" },
    { title: "Building My Path", tips: "Ages 25-35. Career development, relationships, major life decisions" },
    { title: "Trials and Triumphs", tips: "The challenges you faced. Failures that taught you. Successes that changed you" },
    { title: "Love and Relationships", tips: "The people who shaped your heart. Family you created. Friendships that lasted" },
    { title: "Turning Points", tips: "The moments that changed everything. Decisions that altered your trajectory" },
    { title: "Wisdom Earned", tips: "What life has taught you. Lessons you wish you knew earlier" },
    { title: "Where I Stand Today", tips: "Your current life, reflections, hopes for the future. Your legacy" },
  ],
  
  // FICTION - Classic 8-chapter structure
  fiction: [
    { title: "The World Before Things Change", tips: "Introduce protagonist, show ordinary world. Use 'save the cat' moment" },
    { title: "The Inciting Incident", tips: "Something disrupts the ordinary. This is your story's promise to readers" },
    { title: "Crossing the Threshold", tips: "Hero commits to the journey. No turning back. Raise the stakes" },
    { title: "Tests, Allies, and Enemies", tips: "Develop relationships, face challenges. Show character growth through conflict" },
    { title: "The Midpoint Crisis", tips: "Major revelation or reversal. False victory or false defeat. Raise stakes again" },
    { title: "The Dark Night", tips: "Everything falls apart. Hero at lowest point. All seems lost" },
    { title: "The Final Push", tips: "Gather strength, confront the antagonist. Hero uses everything they've learned" },
    { title: "Resolution", tips: "Show the new normal. Character arc complete. Satisfy reader's emotional investment" },
  ],
  
  // MEMOIR - Focused life experience
  memoir: [
    { title: "The Moment Everything Changed", tips: "Start with a vivid scene. Hook readers with the central tension" },
    { title: "Who I Was Before", tips: "Paint your earlier self. Create contrast with who you'll become" },
    { title: "The People Who Shaped Me", tips: "Key relationships. Show don't tell. Use dialogue and specific moments" },
    { title: "The Turning Point", tips: "The event that changed everything. Slow down time here. Every detail matters" },
    { title: "The Struggle", tips: "The hard part of the journey. Be honest about your flaws and mistakes" },
    { title: "Finding My Way", tips: "The breakthrough moment. What shifted in you? Show internal change" },
    { title: "Who I Am Now", tips: "The person you've become. Connect back to opening. Show growth" },
    { title: "Looking Back, Looking Forward", tips: "Final reflections. Universal wisdom. Gift to readers" },
  ],
  
  // BUSINESS - Problem-solution framework
  business: [
    { title: "The Problem", tips: "Define the challenge clearly. Use data and stories. Make readers feel the pain" },
    { title: "Why It Matters", tips: "Quantify the cost of inaction. Create urgency. This is your book's 'why'" },
    { title: "The Solution Framework", tips: "Introduce your methodology. Make it memorable with acronyms or metaphors" },
    { title: "Step One: Foundation", tips: "First actionable step. Include case studies. Make it implementable" },
    { title: "Step Two: Implementation", tips: "The core work. Provide templates, checklists. Anticipate objections" },
    { title: "Step Three: Optimization", tips: "Taking it to next level. Advanced techniques for committed readers" },
    { title: "Common Pitfalls", tips: "What can go wrong. Be honest. Show you understand the challenges" },
    { title: "Your Action Plan", tips: "Summary and next steps. 30-60-90 day plan. Call to action" },
  ],
  
  // CHILDREN'S - Simple story arc
  childrens: [
    { title: "Meet Our Hero", tips: "Introduce main character with a relatable trait. Simple, visual language" },
    { title: "Something Unexpected", tips: "Create a problem to solve. Age-appropriate stakes" },
    { title: "The Adventure Begins", tips: "Get your hero moving. Repetition and rhythm work well" },
    { title: "Things Get Tricky", tips: "The challenge gets harder. Build anticipation" },
    { title: "The Big Moment", tips: "Face the main challenge. Hero uses their special trait" },
    { title: "Happy Ending", tips: "Celebrate the win. Clear moral or warm feeling. Satisfying close" },
  ],
  
  // FREESTYLE - Flexible structure
  freestyle: [
    { title: "Beginning", tips: "Start your story. Hook readers within first page" },
    { title: "Development", tips: "Build your narrative. Develop themes and characters" },
    { title: "Climax", tips: "Reach the peak. Deliver your main impact" },
    { title: "Resolution", tips: "Bring it home. Leave readers satisfied" },
  ],
};

// Word count targets by genre
const WORD_COUNT_TARGETS = {
  shortstory: { min: 1000, max: 7500, perChapter: 1500 },
  lifestory: { min: 40000, max: 80000, perChapter: 5000 },
  fiction: { min: 50000, max: 100000, perChapter: 5000 },
  memoir: { min: 40000, max: 80000, perChapter: 5000 },
  business: { min: 30000, max: 60000, perChapter: 4000 },
  childrens: { min: 500, max: 5000, perChapter: 500 },
  freestyle: { min: 10000, max: 100000, perChapter: 3000 },
};

// Sortable chapter item
function SortableChapterItem({ id, chapter, index, onUpdate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center space-x-3 p-3 bg-white dark:bg-[#22201C] rounded-lg border border-[#E0D8CC] dark:border-[#2A2520]/50 group"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-[#E8E0D4] dark:hover:bg-[#2A2520]/50 rounded"
      >
        <GripVertical className="w-4 h-4 text-[#7C766E] dark:text-[#F0EBE4]/60" />
      </div>
      
      <span className="text-sm font-medium text-[#7C766E] dark:text-[#F0EBE4]/60 w-6">
        {index + 1}.
      </span>
      
      <div className="flex-1 space-y-2">
        <Input
          value={chapter.title}
          onChange={(e) => onUpdate({ ...chapter, title: e.target.value })}
          placeholder="Chapter title"
          className="text-sm dark:bg-[#2A2520] dark:text-[#F0EBE4] dark:border-[#2A2520]"
        />
        <Input
          value={chapter.tips}
          onChange={(e) => onUpdate({ ...chapter, tips: e.target.value })}
          placeholder="Writing tips for this chapter"
          className="text-xs dark:bg-[#2A2520] dark:text-[#F0EBE4]/70 dark:border-[#2A2520]"
        />
      </div>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function SkeletonCustomizerModal({ book, onClose, onGenerate, inline = false }) {
  const [chapters, setChapters] = useState(
    DEFAULT_SKELETONS[book.genre] || DEFAULT_SKELETONS.freestyle
  );
  const [generating, setGenerating] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [numChapters, setNumChapters] = useState(8);
  const [customInstructions, setCustomInstructions] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // AI-powered skeleton generation
  const handleAIGenerate = async () => {
    setAiGenerating(true);
    try {
      const response = await axios.post(
        `${API}/books/${book.book_id}/generate-skeleton`,
        { 
          num_chapters: numChapters,
          custom_instructions: customInstructions.trim() || null
        },
        {timeout: 60000 }
      );
      
      if (response.data.chapters && response.data.chapters.length > 0) {
        setChapters(response.data.chapters);
        toast.success(`AI generated ${response.data.chapters.length} custom chapters!`);
      }
    } catch (error) {
      console.error('AI skeleton generation failed:', error);
      toast.error('Failed to generate AI skeleton. Using default template.');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = chapters.findIndex((_, i) => `chapter-${i}` === active.id);
      const newIndex = chapters.findIndex((_, i) => `chapter-${i}` === over.id);
      setChapters(arrayMove(chapters, oldIndex, newIndex));
    }
  };

  const handleAddChapter = () => {
    setChapters([...chapters, { title: '', tips: '' }]);
  };

  const handleUpdateChapter = (index, updatedChapter) => {
    const newChapters = [...chapters];
    newChapters[index] = updatedChapter;
    setChapters(newChapters);
  };

  const handleDeleteChapter = (index) => {
    if (chapters.length <= 1) {
      toast.error('You need at least one chapter');
      return;
    }
    setChapters(chapters.filter((_, i) => i !== index));
  };

  const handleResetToDefault = () => {
    setChapters(DEFAULT_SKELETONS[book.genre] || DEFAULT_SKELETONS.freestyle);
    toast.success('Reset to default structure');
  };

  const handleGenerate = async () => {
    // Validate
    const emptyChapters = chapters.filter(c => !c.title.trim());
    if (emptyChapters.length > 0) {
      toast.error('Please fill in all chapter titles');
      return;
    }

    setGenerating(true);
    try {
      // Create chapters with custom structure including skeleton guidance
      for (let i = 0; i < chapters.length; i++) {
        const chapterData = {
          title: chapters[i].title,
          chapter_number: i + 1
        };
        
        // Include skeleton guidance if available
        if (chapters[i].tips) {
          chapterData.skeleton = JSON.stringify({
            tips: chapters[i].tips,
            narrative_type: chapters[i].narrative_type,
            emotional_target: chapters[i].emotional_target,
            key_elements: chapters[i].key_elements
          });
        }
        
        await axios.post(
          `${API}/books/${book.book_id}/chapters`,
          chapterData,
          
        );
      }
      
      toast.success(`Created ${chapters.length} chapters with guidance!`);
      onGenerate();
      onClose();
    } catch (error) {
      console.error('Failed to generate chapters:', error);
      toast.error('Failed to create chapters');
    } finally {
      setGenerating(false);
    }
  };

  if (inline) {
    return (
      <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-2xl overflow-hidden">
        {/* AI Generation Panel */}
        <div className="px-4 sm:px-6 py-4 border-b border-[#E0D8CC] dark:border-[#2A2520]/50">
          <div className="flex items-center space-x-2 mb-3">
            <Wand2 className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
            <span className="text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">AI Story Architect</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={numChapters}
              onChange={(e) => setNumChapters(parseInt(e.target.value))}
              className="px-3 py-2 rounded-lg border border-[#E0D8CC] dark:border-[#2A2520] bg-white dark:bg-[#2A2520] text-[#2A2420] dark:text-[#F0EBE4] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C4D6B]"
            >
              <option value={4}>4 chapters</option>
              <option value={6}>6 chapters</option>
              <option value={8}>8 chapters</option>
              <option value={10}>10 chapters</option>
              <option value={12}>12 chapters</option>
              <option value={15}>15 chapters</option>
              <option value={20}>20 chapters</option>
            </select>
            <Input
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="Special instructions (optional)..."
              className="flex-1 text-sm dark:bg-[#2A2520] dark:text-[#F0EBE4]"
            />
            <Button
              onClick={handleAIGenerate}
              disabled={aiGenerating}
              className="bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white whitespace-nowrap"
            >
              {aiGenerating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Generate with AI</>
              )}
            </Button>
          </div>
        </div>

        {/* Chapter list */}
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#7C766E] dark:text-[#8C847A]">{chapters.length} chapters · Drag to reorder</p>
            <Button variant="outline" size="sm" onClick={handleResetToDefault} className="text-xs">Reset</Button>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={chapters.map((_, i) => `chapter-${i}`)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {chapters.map((chapter, index) => (
                  <SortableChapterItem
                    key={`chapter-${index}`}
                    id={`chapter-${index}`}
                    chapter={chapter}
                    index={index}
                    onUpdate={(updated) => handleUpdateChapter(index, updated)}
                    onDelete={() => handleDeleteChapter(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <Button variant="outline" onClick={handleAddChapter} className="w-full mt-3 border-dashed border-2 border-[#C4B8D0] dark:border-[#3A3040] text-[#5C4D6B] dark:text-[#B0A0C0] hover:bg-[#5C4D6B]/5">
            <Plus className="w-4 h-4 mr-2" />Add Chapter
          </Button>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 border-t border-[#E0D8CC] dark:border-[#2A2520]/50 bg-[#F8F5F0] dark:bg-[#1C1A18]">
          <Button
            onClick={handleGenerate}
            disabled={generating || chapters.length === 0}
            data-testid="start-writing-now-button"
            className="w-full bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white font-semibold py-5"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating chapters...</>
            ) : (
              <><PenLine className="w-4 h-4 mr-2" />Start Writing ({chapters.length} chapters)</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="bg-[#F8F5F0] dark:bg-[#1A1816] rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-[#E0D8CC] dark:border-[#2A2520]/50 bg-[#F8F5F0] dark:bg-[#1C1A18] rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#5C4D6B] dark:bg-[#7B6A90] rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-[#2A2420] dark:text-[#F0EBE4]">
                  Chapter Plan
                </h2>
                <p className="text-xs sm:text-sm text-[#2A2420]/60 dark:text-[#F0EBE4]/60">
                  for "{book.title}"
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-[#2A2420] dark:text-[#F0EBE4] hover:text-[#2A2420] p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* AI Generation Panel */}
        <div className="px-4 sm:px-6 py-4 bg-white dark:bg-[#22201C] border-b border-[#E0D8CC] dark:border-[#2A2520]/50">
          <div className="flex items-center space-x-2 mb-3">
            <Wand2 className="w-5 h-5 text-[#5C4D6B] dark:text-[#B0A0C0]" />
            <span className="font-semibold text-[#2A2420] dark:text-[#F0EBE4]">AI Story Architect</span>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-[#7C766E] dark:text-[#8C847A] mb-2">
              Number of Chapters
            </label>
            <select
              value={numChapters}
              onChange={(e) => setNumChapters(parseInt(e.target.value))}
              className="w-full px-4 py-3 rounded-lg border border-[#E0D8CC] dark:border-[#2A2520] bg-white dark:bg-[#2A2520] text-[#2A2420] dark:text-[#F0EBE4] text-base font-medium focus:outline-none focus:ring-2 focus:ring-[#5C4D6B]"
            >
              <option value={4}>4 chapters</option>
              <option value={6}>6 chapters</option>
              <option value={8}>8 chapters</option>
              <option value={10}>10 chapters</option>
              <option value={12}>12 chapters</option>
              <option value={15}>15 chapters</option>
              <option value={20}>20 chapters</option>
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-xs font-medium text-[#7C766E] dark:text-[#8C847A] mb-1">
              Special Instructions (optional)
            </label>
            <Input
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="e.g., Include a twist ending..."
              className="text-sm dark:bg-[#22201C] py-3"
            />
          </div>
          
          <Button
            onClick={handleAIGenerate}
            disabled={aiGenerating}
            className="w-full bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white"
          >
            {aiGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                AI is crafting your story structure...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Custom Chapters with AI
              </>
            )}
          </Button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <p className="text-xs sm:text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70">
              {chapters.length} chapters • Drag to reorder
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetToDefault}
              className="text-xs"
            >
              Reset to Default
            </Button>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={chapters.map((_, i) => `chapter-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {chapters.map((chapter, index) => (
                  <SortableChapterItem
                    key={`chapter-${index}`}
                    id={`chapter-${index}`}
                    chapter={chapter}
                    index={index}
                    onUpdate={(updated) => handleUpdateChapter(index, updated)}
                    onDelete={() => handleDeleteChapter(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <Button
            variant="outline"
            onClick={handleAddChapter}
            className="w-full mt-4 border-dashed border-2 border-[#C4B8D0] dark:border-[#3A3040] text-[#5C4D6B] dark:text-[#B0A0C0] hover:bg-[#5C4D6B]/5 dark:hover:bg-[#B0A0C0]/5"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Chapter
          </Button>
        </div>

        {/* Footer - Always visible at bottom */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-[#E0D8CC] dark:border-[#2A2520]/50 bg-white dark:bg-[#22201C] rounded-b-2xl">
          {/* Start Writing - Primary action */}
          <Button
            onClick={handleGenerate}
            disabled={generating || chapters.length === 0}
            data-testid="start-writing-now-button"
            className="w-full mb-3 py-6 text-lg bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white font-semibold"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating chapters...
              </>
            ) : (
              <>
                <PenLine className="w-5 h-5 mr-2" />
                Start Writing ({chapters.length} chapters)
              </>
            )}
          </Button>
          
          {/* Secondary actions */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#2A2420]/60 dark:text-[#F0EBE4]/60">
              {chapters.length} chapters ready
            </p>
            <Button variant="outline" onClick={onClose} size="sm">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SkeletonCustomizerModal;
